import { ConnecteeSubmission, MATCH_STATUS, Provider, SUBMISSION_STATUS } from "../types/connecteeSubmission";

const createdAt = new Date().toISOString();

function mockProvider(overrides: Partial<Provider> & Pick<Provider, "id" | "firstName" | "lastName">): Provider {
  const { firstName, lastName, id, ...rest } = overrides;
  return {
    id,
    uuid: rest.uuid ?? `mock-uuid-${id}`,
    firstName,
    lastName,
    fullName: rest.fullName ?? `${firstName} ${lastName}`,
    email: rest.email ?? `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
    phone: rest.phone ?? "+15551234567",
    avatar: rest.avatar ?? null,
    avatarThumb: rest.avatarThumb ?? null,
    professionalCredentials: rest.professionalCredentials ?? "Licensed Clinical Psychologist",
    otherProfessionalCredentials: rest.otherProfessionalCredentials ?? null,
    providerProfileInfo: rest.providerProfileInfo ?? {
      clinicalSpecialties: ["Anxiety", "Depression", "Trauma-Informed"],
    },
    organization: rest.organization ?? { id: 1, name: "Example Practice" },
  };
}

const sarah = mockProvider({
  id: 1,
  uuid: '76ef215e-34c9-48ac-96d5-b244c9e1b0f6',
  firstName: "Sarah",
  lastName: "Jenkins",
  fullName: "Dr. Sarah Jenkins",
  email: "sarah.jenkins@example.com",
  phone: "+15551234567",
  avatar: "https://i.pravatar.cc/360?img=9",
  avatarThumb: "https://i.pravatar.cc/256?img=9",
  professionalCredentials: "Licensed Clinical Psychologist",
  providerProfileInfo: {
    clinicalSpecialties: ["Anxiety", "Depression", "Trauma-Informed"],
  },
});

const marcus = mockProvider({
  id: 2,
  uuid: '2f5c905d-b940-493e-b279-ef5128ca266c',
  firstName: "Marcus",
  lastName: "Sterling",
  fullName: "Marcus Sterling, LCSW",
  email: "m.sterling@example.com",
  phone: "+15559876543",
  avatar: "https://i.pravatar.cc/360?img=65",
  avatarThumb: "https://i.pravatar.cc/256?img=65",
  professionalCredentials: "Clinical Social Worker",
  providerProfileInfo: {
    clinicalSpecialties: ["Life Transitions", "Relationships", "Mindfulness"],
  },
});

const jordan = mockProvider({
  id: 3,
  uuid: '598e49b1-ad5e-4a71-8bf9-87242fd7821c',
  firstName: "Jordan",
  lastName: "Chen",
  fullName: "Jordan Chen, LMFT",
  email: "j.chen@example.com",
  phone: "+15554567890",
  // intentionally no avatar — covers the empty state
  professionalCredentials: "Marriage & Family Therapist",
  providerProfileInfo: {
    clinicalSpecialties: ["Family Dynamics", "LGBTQ+ Affirming", "Stress Management"],
  },
});

const baseSubmission = {
  id: 9001,
  email: "sarah.test@example.com",
  phone: "+15550001111",
  firstName: "Sarah",
  lastName: "Test",
  createdAt,
  accessToken: "dev-mock-token",
};

export const mockMatchFoundSubmission: ConnecteeSubmission = {
  ...baseSubmission,
  status: SUBMISSION_STATUS.IN_PROGRESS,
  connecteeMatches: [
    {
      id: 1,
      providerId: sarah.id,
      connecteeSubmissionId: baseSubmission.id,
      status: MATCH_STATUS.SENT_TO_PROVIDER,
      provider: sarah,
    },
  ],
};

export const mockFallbackMatchesSubmission: ConnecteeSubmission = {
  ...baseSubmission,
  id: 9002,
  status: SUBMISSION_STATUS.FALLBACK,
  connecteeMatches: [
    {
      id: 1,
      providerId: sarah.id,
      connecteeSubmissionId: 9002,
      status: MATCH_STATUS.SATISFACTORY_FALLBACK,
      provider: sarah,
    },
    {
      id: 2,
      providerId: marcus.id,
      connecteeSubmissionId: 9002,
      status: MATCH_STATUS.SATISFACTORY_FALLBACK,
      provider: marcus,
    },
    {
      id: 3,
      providerId: jordan.id,
      connecteeSubmissionId: 9002,
      status: MATCH_STATUS.SATISFACTORY_FALLBACK,
      provider: jordan,
    },
  ],
};

export const mockNoMatchesSubmission: ConnecteeSubmission = {
  ...baseSubmission,
  id: 9003,
  status: SUBMISSION_STATUS.FALLBACK,
  connecteeMatches: [],
};
